import { useEffect } from 'react'
import { useAdminStore } from '../../core/stores/adminStore'
// Note: Backend doesn't have separate /admin/* endpoints
// Admin functionality is role-based using existing services
// TODO: Implement admin endpoints in backend or use existing services with role checks

import { Card } from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import { 
  UsersIcon, 
  BuildingStorefrontIcon, 
  ShoppingCartIcon, 
  CurrencyDollarIcon,
  CreditCardIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon
} from '@heroicons/react/24/outline'

export default function AdminDashboardPage() {
  const { 
    systemStats, 
    setSystemStats,
    pendingPayments,
    setPendingPayments,
    setPendingPaymentsLoading,
    recentOrders,
    setRecentOrders,
    setRecentOrdersLoading
  } = useAdminStore()

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      // TODO: Backend doesn't have /admin/* endpoints yet
      // Admin functionality should be implemented in backend with role-based authorization
      // For now, using placeholder data or existing services
      
      // Placeholder: Set empty stats (backend needs to implement /admin/stats endpoint)
      // setSystemStats({
      //   totalBusinesses: 0,
      //   totalUsers: 0,
      //   totalOrders: 0,
      //   totalRevenue: 0,
      //   pendingPayments: 0
      // })
      
      // Placeholder: Empty arrays (backend needs to implement these endpoints)
      setPendingPayments([])
      setRecentOrders([])
      
      console.warn('Admin endpoints not implemented in backend yet')
    } catch (error) {
      console.error('Failed to load dashboard data:', error)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const formatGrowth = (growth: number) => {
    const isPositive = growth >= 0
    return (
      <span className={`flex items-center ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {isPositive ? (
          <ArrowTrendingUpIcon className="w-4 h-4 mr-1" />
        ) : (
          <ArrowTrendingDownIcon className="w-4 h-4 mr-1" />
        )}
        {Math.abs(growth).toFixed(1)}%
      </span>
    )
  }

  if (!systemStats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600">System overview and management</p>
        </div>
        <Button
          variant="outline"
          onClick={loadDashboardData}
          className="flex items-center"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UsersIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Users</p>
              <p className="text-2xl font-semibold text-gray-900">
                {systemStats.totalUsers.toLocaleString()}
              </p>
              <p className="text-sm">
                {formatGrowth(systemStats.monthlyGrowth.users)} this month
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BuildingStorefrontIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Businesses</p>
              <p className="text-2xl font-semibold text-gray-900">
                {systemStats.totalBusinesses.toLocaleString()}
              </p>
              <p className="text-sm">
                {formatGrowth(systemStats.monthlyGrowth.businesses)} this month
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ShoppingCartIcon className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Orders</p>
              <p className="text-2xl font-semibold text-gray-900">
                {systemStats.totalOrders.toLocaleString()}
              </p>
              <p className="text-sm">
                {formatGrowth(systemStats.monthlyGrowth.orders)} this month
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CurrencyDollarIcon className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Revenue</p>
              <p className="text-2xl font-semibold text-gray-900">
                {formatCurrency(systemStats.totalRevenue)}
              </p>
              <p className="text-sm">
                {formatGrowth(systemStats.monthlyGrowth.revenue)} this month
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Quick Actions</h3>
            <ClockIcon className="h-6 w-6 text-gray-400" />
          </div>
          <div className="space-y-3">
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start"
              onClick={() => window.location.href = '/admin/payments'}
            >
              Review Pending Payments ({systemStats.pendingPayments})
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start"
              onClick={() => window.location.href = '/admin/users'}
            >
              Manage Users
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start"
              onClick={() => window.location.href = '/admin/analytics'}
            >
              View Analytics
            </Button>
          </div>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Payments */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Pending Payments</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.href = '/admin/payments'}
            >
              View All
            </Button>
          </div>
          <div className="space-y-3">
            {pendingPayments.length === 0 ? (
              <p className="text-sm text-gray-500">No pending payments</p>
            ) : (
              pendingPayments.slice(0, 5).map((payment) => (
                <div key={payment.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                  <div>
                    <p className="text-sm font-medium">Payment #{payment.id}</p>
                    <p className="text-xs text-gray-500">
                      {formatCurrency(payment.amount)} • {new Date(payment.uploadedAt).toLocaleDateString('en-US', { calendar: 'gregory' })}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <Button size="xs" variant="primary">
                      Review
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Recent Orders */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Recent Orders</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.href = '/admin/orders'}
            >
              View All
            </Button>
          </div>
          <div className="space-y-3">
            {recentOrders.length === 0 ? (
              <p className="text-sm text-gray-500">No recent orders</p>
            ) : (
              recentOrders.slice(0, 5).map((order) => (
                <div key={order.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                  <div>
                    <p className="text-sm font-medium">{order.businessName}</p>
                    <p className="text-xs text-gray-500">
                      {order.customerName} • {formatCurrency(order.totalAmount)}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      order.status === 'completed' ? 'bg-green-100 text-green-800' :
                      order.status === 'preparing' ? 'bg-yellow-100 text-yellow-800' :
                      order.status === 'ready' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {order.status}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(order.createdAt).toLocaleDateString('en-US', { calendar: 'gregory' })}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  )
}